import { ActionTree } from 'vuex'
import { CancelConsultationRequest, ScheduleState } from './types'
import axios from 'axios'
import { ApiVersion, RootState } from '@/store/types'
import { Response } from '@/store/response'
import {
  DoctorModuleItem,
  ModuleConsultationRequest,
  AnswerConsultationRequest,
  PendingConsultationItem
} from '@/store/consultories/types'
import { ScheduleMutations } from './mutations'
import { NotificationType, showNotification } from '@/utils/Notification'
import {
  EndConsultationRequest,
  StartConsultationRequest
} from '../patient/types'

export const actions: ActionTree<ScheduleState, RootState> = {
  //Get all the modules by doctor
  async getAllModules({ commit }) {
    try {
      commit(ScheduleMutations.SET_LOADER_DOCTOR_MODULES, false)
      const { data } = await axios(`${ApiVersion.V1}/Consultories/modules`)
      const response: Response<Array<DoctorModuleItem>> = data
      commit(ScheduleMutations.SET_ALL_DOCTOR_MODULES, response.data)
    } catch (error) {
      console.error(error)
    } finally {
      commit(ScheduleMutations.SET_LOADER_DOCTOR_MODULES, false)
    }
  },

  //get pending consultations
  async getPendingConsultations({ commit }) {
    try {
      commit(ScheduleMutations.SET_LOADER_PENDING_CONSULTATIONS, false)
      const { data } = await axios(`${ApiVersion.V1}/Consultations/pendings`)
      let response: Response<Array<PendingConsultationItem>> = data

      response?.data?.forEach((item: PendingConsultationItem) => {
        if (item.startDate) {
          item.startDate = fixDateFormat(item.startDate)
        }
        if (item.endDate) {
          item.endDate = fixDateFormat(item.endDate)
        }
      })

      commit(ScheduleMutations.SET_PENDING_CONSULTATIONS, response.data)
    } catch (error) {
      console.error(error)
    } finally {
      commit(ScheduleMutations.SET_LOADER_PENDING_CONSULTATIONS, false)
    }
  },

  //Get all module consultations
  async getAllConsultationssByModule(
    { commit },
    request: ModuleConsultationRequest
  ) {
    try {
      commit(ScheduleMutations.SET_LOADER_SCHEDULED_CONSULTATIONS, true)
      let url = `${ApiVersion.V1}/Consultations/${request.moduleId}`
      if (request.year != null) {
        url += `?year=${request.year}`
      }

      if (request.filterBy != null) {
        url += `&filterBy=${request.filterBy}`
      }

      if (request.filterParam != null) {
        url += `&filterParam=${request.filterParam}`
      }
      const { data } = await axios(url)
      const response: Response<Array<PendingConsultationItem>> = data

      response?.data?.forEach((item: PendingConsultationItem) => {
        if (item.startDate) {
          item.startDate = fixDateFormat(item.startDate)
        }
        if (item.endDate) {
          item.endDate = fixDateFormat(item.endDate)
        }
      })

      console.log(response.data)

      commit(ScheduleMutations.SET_SCHEDULED_CONSULTATIONS, response.data)
    } catch (error) {
      console.error(error)
    } finally {
      commit(ScheduleMutations.SET_LOADER_SCHEDULED_CONSULTATIONS, false)
    }
  },

  //Refuse consultation
  async refuseConsultation({ dispatch }, request: AnswerConsultationRequest) {
    try {
      const { data } = await axios.post(
        `${ApiVersion.V1}/Consultations/${request.eventId}/refuse/${request.reasonId}`
      )
      const response: Response<any> = data
      if (response.succeeded) {
        showNotification(NotificationType.SUCCESS, response.message)
        dispatch('getPendingConsultations')
      } else {
        showNotification(NotificationType.WARNING, response.message)
      }
    } catch (error) {
      console.error(error)
    }
  },

  //Accept consultation
  async acceptConsultation({ dispatch }, request: AnswerConsultationRequest) {
    try {
      const { data } = await axios.post(
        `${ApiVersion.V1}/Consultations/${request.eventId}/accept`
      )
      const response: Response<any> = data
      if (response.succeeded) {
        showNotification(NotificationType.SUCCESS, response.message)
        dispatch('getPendingConsultations')
      } else {
        showNotification(NotificationType.WARNING, response.message)
      }
    } catch (error) {
      console.error(error)
    }
  },

  //Start consultation
  async startConsultation({ dispatch }, request: StartConsultationRequest) {
    try {
      console.log('Inicio')
      const { data } = await axios.post(
        `${ApiVersion.V1}/Consultations/${request.scheduleEventId}/start`
      )
      const response: Response<any> = data
      if (response.succeeded) {
        showNotification(NotificationType.SUCCESS, response.message, 2000)
        await dispatch('patients/getConsultations', request.patientId, {
          root: true
        })
      } else {
        showNotification(NotificationType.WARNING, response.message, 2000)
      }

      return response?.succeeded
    } catch (error) {
      showNotification(
        NotificationType.WARNING,
        'Error al iniciar la consulta',
        2000
      )
      console.error(error)
      return false
    }
  },

  //Cancel consultation
  async cancelConsultation(
    { dispatch, commit },
    request: CancelConsultationRequest
  ) {
    try {
      commit(ScheduleMutations.SET_LOADER_SCHEDULED_CONSULTATIONS, true)
      const tag = request.hours > 48 ? '48hrs-or-more' : '48hrs-or-less'
      const { data } = await axios.post(
        `${ApiVersion.V1}/Consultations/${request.eventId}/cancel/${tag}/${request.reasonId}`
      )
      const response: Response<string> = data
      console.log(response)
      if (response.succeeded) {
        showNotification(NotificationType.SUCCESS, response.message)
        dispatch('getPendingConsultations')
      }
    } catch (error) {
      console.error(error)
      showNotification(
        NotificationType.WARNING,
        'No fue posible iniciar la consulta'
      )
    } finally {
      commit(ScheduleMutations.SET_LOADER_SCHEDULED_CONSULTATIONS, false)
    }
  },

  //End consultation
  async endConsultation({ dispatch }, request: EndConsultationRequest) {
    try {
      const { data } = await axios.post(
        `${ApiVersion.V1}/Consultations/${request.scheduleEventId}/end`
      )
      const response: Response<String> = data
      if (response.succeeded) {
        showNotification(NotificationType.SUCCESS, response.message, 2000)
        dispatch('patients/getConsultations', request.patientId, { root: true })
      }
    } catch (error) {
      showNotification(
        NotificationType.WARNING,
        'Error al cancelar la consulta',
        2000
      )
    }
  },

  //End consultation
  async changeVisibilityRequests({ commit }, status: Boolean) {
    try {
      commit(ScheduleMutations.SHOW_REQUESTS, status)
    } catch (error) {
      console.error(error)
    }
  }
}

function fixDateFormat(date: string): string {
  // Reemplaza el formato "YYYY-M-D H:mm:ss" con "YYYY-MM-DDTHH:mm:ss"
  return date.replace(/(\d{4})-(\d{1,2})-(\d{1,2})/, (_, y, m, d) => {
    // Asegúrate de que los meses y días sean siempre de 2 dígitos
    const month = m.padStart(2, '0')
    const day = d.padStart(2, '0')
    return `${y}-${month}-${day}`
  })
}
